import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { safeAuthDestination } from "@/lib/roomTerms";
import { useI18n, SUPPORTED_LANGS } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { TESTIDS } from "@/constants/testIds";
import { ArrowRight } from "lucide-react";

export default function AuthPage({ mode: initialMode = "signin" }) {
  const { t, lang, setLang } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const { login, register } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({ email: "", password: "", full_name: "", company: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      if (mode === "signin") await login(form.email, form.password);
      else {
        const result = await register({ ...form, language: lang });
        if (result.email_verification_required) {
          nav(`/verify-email?email=${encodeURIComponent(form.email)}`, { replace: true });
          return;
        }
      }
      const dest = safeAuthDestination(loc.state?.from);
      nav(dest, { replace: true });
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      if (detail?.code === "email_verification_required") {
        nav(`/verify-email?email=${encodeURIComponent(form.email)}`);
        return;
      }
      setErr(typeof detail === "string" ? detail : t("auth_err_login"));
    } finally {
      setLoading(false);
    }
  };

  const cycleLang = () => {
    const idx = SUPPORTED_LANGS.indexOf(lang);
    setLang(SUPPORTED_LANGS[(idx + 1) % SUPPORTED_LANGS.length]);
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-12 hairline-top" style={{ background: "var(--td-surface)" }}>
        <Link to="/" className="flex items-center gap-3">
          <div className="w-2.5 h-2.5 bg-[var(--td-gold)] rotate-45" />
          <span className="font-serif text-xl">TradeDocs<span className="text-[var(--td-gold)]"> · </span>Desk</span>
        </Link>
        <div>
          <h2 className="font-serif text-5xl leading-tight mb-6">
            {t("auth_hero_title")}
          </h2>
          <p className="text-[var(--td-text-2)] max-w-md leading-relaxed">
            {t("auth_hero_sub")}
          </p>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-text-3)]">
          {t("auth_trust_line")}
        </div>
      </div>

      <div className="flex items-center justify-center px-4 py-20 sm:p-8 md:p-16 relative">
        <button
          onClick={cycleLang}
          data-testid={TESTIDS.langToggle}
          className="absolute top-6 right-6 font-mono text-xs px-2 py-1 border border-[var(--td-border)] hover:border-[var(--td-gold)] hover:text-[var(--td-gold)] transition-colors uppercase tracking-widest"
        >
          {lang.toUpperCase()}
        </button>

        <form onSubmit={submit} className="w-full max-w-md space-y-5">
          <div className="kicker mb-2">— {mode === "signin" ? t("auth_signin") : t("auth_signup")}</div>
          <h1 className="font-serif text-3xl sm:text-4xl mb-6">
            {mode === "signin" ? t("auth_welcome_back") : t("auth_open_desk")}
          </h1>

          {mode === "signup" && (
            <>
              <div>
                <label className="td-label">{t("auth_name")}</label>
                <input
                  required
                  data-testid={TESTIDS.authName}
                  className="td-input"
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                />
              </div>
              <div>
                <label className="td-label">{t("auth_company")}</label>
                <input
                  data-testid={TESTIDS.authCompany}
                  className="td-input"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                />
              </div>
            </>
          )}
          <div>
            <label className="td-label">{t("auth_email")}</label>
            <input
              required type="email"
              data-testid={TESTIDS.authEmail}
              className="td-input"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </div>
          <div>
            <label className="td-label">{t("auth_password")}</label>
            <input
              required type="password" minLength={12}
              data-testid={TESTIDS.authPassword}
              className="td-input"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
          </div>

          {mode === "signin" && (
            <div className="text-right -mt-2">
              <Link to="/forgot-password" className="text-xs text-[var(--td-text-2)] hover:text-[var(--td-gold)] transition-colors">
                {t("auth_forgot_password")}
              </Link>
            </div>
          )}

          <p className="text-[11px] leading-relaxed text-[var(--td-text-3)]">
            {mode === "signup" ? t("auth_privacy_signup") : t("auth_privacy_signin")}{" "}
            <Link to="/privacy-policy" className="text-[var(--td-gold)] hover:underline underline-offset-4">
              {t("ft_link_privacy")}
            </Link>
            {mode === "signup" && (
              <>
                {" · "}
                <Link to="/legal/terms" className="text-[var(--td-gold)] hover:underline underline-offset-4">
                  {t("ft_link_terms")}
                </Link>
              </>
            )}
          </p>

          {err && <div data-testid={TESTIDS.authError} className="text-sm text-red-400">{err}</div>}

          <button
            type="submit"
            data-testid={TESTIDS.authSubmit}
            disabled={loading}
            className="btn-gold w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? t("auth_loading") : (
              <>
                {mode === "signin" ? t("auth_login_btn") : t("auth_register_btn")}
                <ArrowRight size={14} />
              </>
            )}
          </button>

          <button
            type="button"
            data-testid={TESTIDS.authToggle}
            onClick={() => { setMode(mode === "signin" ? "signup" : "signin"); setErr(""); }}
            className="w-full text-sm text-[var(--td-text-2)] hover:text-[var(--td-gold)] transition-colors"
          >
            {mode === "signin"
              ? `${t("auth_no_account")} ${t("auth_register_btn")}`
              : `${t("auth_have_account")} ${t("auth_login_btn")}`}
          </button>
        </form>
      </div>
    </div>
  );
}
